import React, { MouseEvent, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import DefaultActivityTypeName from "@mapmycustomers/shared/enum/activity/DefaultActivityTypeName";
import { Activity, Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";
import { OnlyRequiredFields } from "@mapmycustomers/shared/util/ts";
import { Button } from "@mapmycustomers/ui";

import {
  createActivity,
  showGlobalCreateActivityModal,
} from "@app/component/createEditEntity/Activity/store/actions";
import { getActiveActivityTypes } from "@app/store/activity";
import { getCurrentUser } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { activityLayoutModel } from "@app/util/layout/impl";
import loggingService from "@app/util/logging";
import { parsePhoneNumber } from "@app/util/parsers";

import styles from "./Contacts.module.scss";

interface Props {
  activityTypes: ActivityType[];
  company?: Company;
  currentUser: User;
  onCreateActivity: typeof createActivity.request;
  onShowCreateActivityModal: typeof showGlobalCreateActivityModal;
  person?: Person;
  phoneNumber?: string;
}

const PhoneButton: React.FC<Props> = ({
  activityTypes,
  company,
  currentUser,
  onCreateActivity,
  onShowCreateActivityModal,
  person,
  phoneNumber,
}) => {
  const analyticService = useAnalytics();
  const intl = useIntl();
  const entity = company ?? person;
  const phone = entity?.phone ?? phoneNumber;
  const parsedPhoneNumber = parsePhoneNumber(phone);

  if (!company && !person && !phoneNumber) {
    loggingService.warning(
      "PhoneButton should have either company or person or phoneNumber prop defined"
    );
  }

  const callActivityType = useMemo(
    () => activityTypes.find((activity) => activity.name === DefaultActivityTypeName.CALL),
    [activityTypes]
  );

  const activity: OnlyRequiredFields<
    Omit<Activity, "files">,
    "crmActivityType" | "entity"
  > = useMemo(() => {
    const date = new Date();
    return {
      account: company ?? undefined,
      assignee: currentUser,
      assigneeId: currentUser.id,
      completed: true,
      contact: person ?? undefined,
      crmActivityType: callActivityType!,
      entity: EntityType.ACTIVITY,
      name: `${DefaultActivityTypeName.CALL} ${entity?.name ?? ""}`,
      startAt: date.toISOString(),
    };
  }, [company, currentUser, entity, person, callActivityType]);

  const handleCallClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation();
      analyticService?.clicked(["Phone Click"], { phone });
    },
    [analyticService, phone]
  );

  const handleLogActivity = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      handleCallClick(e);
      // this shouldn't really happen, but still being cautious
      if (!callActivityType) {
        onShowCreateActivityModal({ activity });
        return;
      }

      const hasRequiredFields = activityLayoutModel.hasMissingRequiredFieldsFor(activity);
      if (hasRequiredFields) {
        onShowCreateActivityModal({ activity });
      } else {
        onCreateActivity({
          activity,
          customFieldsValues: [],
          layoutId: activityLayoutModel.defaultLayout.id,
        });
      }
    },
    [activity, callActivityType, handleCallClick, onCreateActivity, onShowCreateActivityModal]
  );

  const isDisabled = !parsedPhoneNumber;

  return (
    <Tooltip
      title={
        isDisabled
          ? intl.formatMessage({
              id: "entityPreview.contacts.phone.noPhoneNumber",
              defaultMessage: "No phone number",
              description: "Tooltip for the Call button in Contacts section in Preview pane",
            })
          : phone
      }
    >
      {callActivityType ? (
        <Dropdown
          disabled={isDisabled}
          overlay={
            <Menu>
              <Menu.Item key="call-with-activity">
                <Button
                  className={styles.logActivity}
                  href={parsedPhoneNumber?.link}
                  onClick={handleLogActivity}
                  type="link"
                >
                  {intl.formatMessage({
                    id: "recordViewPane.quickActions.call.logActivity.menuItem",
                    defaultMessage: "Call and log activity",
                    description: "Menu Item for call and log activity",
                  })}
                </Button>
              </Menu.Item>
              <Menu.Item key="call-without-activity">
                <Button
                  className={styles.logActivity}
                  href={parsedPhoneNumber?.link}
                  onClick={handleCallClick}
                  type="link"
                >
                  {intl.formatMessage({
                    id: "recordViewPane.quickActions.onlyCall.menuItem",
                    defaultMessage: "Call but don’t log activity",
                    description: "Menu Item for not logging activity",
                  })}
                </Button>
              </Menu.Item>
            </Menu>
          }
          trigger={isDisabled ? [] : ["click"]}
        >
          <Button className={styles.textFieldButton} disabled={isDisabled} size="small" type="link">
            <div className={styles.textFieldIcon}>
              <FontAwesomeIcon className={styles.iconColor} icon={faPhone} />
            </div>
          </Button>
        </Dropdown>
      ) : (
        <Button
          className={styles.textFieldButton}
          disabled={isDisabled}
          href={parsedPhoneNumber?.link}
          onClick={handleCallClick}
          type="link"
        >
          <div className={styles.textFieldIcon}>
            <FontAwesomeIcon className={styles.iconColor} icon={faPhone} />
          </div>
        </Button>
      )}
    </Tooltip>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActiveActivityTypes(state),
  currentUser: getCurrentUser(state)!,
});

const mapDispatchToProps = {
  onCreateActivity: createActivity.request,
  onShowCreateActivityModal: showGlobalCreateActivityModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(PhoneButton);
